import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, AlertTriangle, Check } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";

const fmt = (s) => (s ? new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");
const tone = (p) => (p == null ? "bg-slate-300" : p >= 100 ? "bg-rose-600" : p >= 80 ? "bg-amber-500" : "bg-emerald-600");

const Row = ({ r, onAck, onOpen }) => {
  const pending = r.alert?.pending || 0;
  return (
    <div className="flex flex-wrap items-center gap-3 py-2.5 cursor-pointer hover:bg-slate-50 px-2 -mx-2 rounded" onClick={() => onOpen(r.id)} data-testid={`usage-row-${r.slug}`}>
      <span className="font-semibold text-sm w-56 truncate">{r.name}{r.is_demo && <span className="text-[10px] text-slate-500"> · démo</span>}</span>
      <span className="text-xs text-slate-500 w-32 truncate">{r.tier_label || (r.agent_limit ? "Limite manuelle" : "Sans limite")}</span>
      <div className="flex-1 min-w-[140px] h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${tone(r.pct)} transition-[width] duration-500`} style={{ width: `${Math.min(r.pct ?? 0, 100)}%` }} /></div>
      <span className="font-display font-bold text-sm w-24 text-right" data-testid={`usage-count-${r.slug}`}>{r.agents_active}{r.agent_limit ? ` / ${r.agent_limit}` : ""}</span>
      {pending > 0 ? (
        <span className="inline-flex items-center gap-2 text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700" data-testid={`usage-alert-${r.slug}`}>
          <AlertTriangle className="h-3 w-3" />{pending} tentative(s) de dépassement · {fmt(r.alert.last_at)}
          <button type="button" onClick={(e) => { e.stopPropagation(); onAck(r.id); }} className="inline-flex items-center gap-0.5 underline" data-testid={`usage-ack-${r.slug}`}><Check className="h-3 w-3" />Vu</button>
        </span>
      ) : r.pct >= 80 ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`usage-near-${r.slug}`}>{r.pct >= 100 ? "Limite atteinte" : "Proche de la limite"}</span> : null}
    </div>
  );
};

export const AgentUsagePanel = ({ onOpen }) => {
  const { lastEvent } = useRealtime();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => http.get("/superadmin/agent-usage").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (["agent_limit_attempt", "company_changed"].includes(lastEvent?.event)) load(); }, [lastEvent, load]);
  const ack = (id) => http.post(`/superadmin/agent-usage/${id}/ack`).then(load, (e) => toast.error(formatApiError(e)));
  const total = rows.reduce((a, r) => a + r.agents_active, 0);
  const alerts = rows.filter((r) => r.alert?.pending > 0).length;
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="agent-usage-panel">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><Gauge className="h-5 w-5 text-[#C9962E]" />Suivi des agents actifs</h2>
        <span className="text-xs text-slate-500" data-testid="usage-total">{total} agents actifs au total · archivés non comptés</span>
        {alerts > 0 && <span className="ml-auto text-xs font-bold text-rose-700" data-testid="usage-alerts-count">{alerts} entreprise(s) ont tenté de dépasser leur limite</span>}
      </div>
      <div className="divide-y divide-slate-100">{rows.map((r) => <Row key={r.id} r={r} onAck={ack} onOpen={onOpen} />)}</div>
    </section>
  );
};
